import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BoomVerifiedBadge,
  RunClubBadge,
  Sticker,
  Wordmark,
} from "@/components/runboom/marks";
import { OracleCard } from "@/components/runboom/oracle-card";
import { RaceCard } from "@/components/runboom/race-card";
import { LeaderboardRow } from "@/components/runboom/leaderboard-row";
import { WallEvent } from "@/components/runboom/wall-event";
import {
  heroPrediction,
  heroRunner,
  leaderboard,
  upcomingRaces,
  wall,
} from "@/lib/mock";

const HOME_HERO_IMG =
  "/brand/brand-motion-crop.png";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <TopBar />

      <section className="relative mt-6 overflow-hidden rounded-[18px] border border-border bg-night shadow-[0_20px_80px_rgba(0,0,0,0.45)] sm:mt-10">
        <HeroImage />
        <div className="relative grid grid-cols-1 gap-6 p-6 sm:p-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-blood-orange" style={{ color: "var(--color-blood-orange)" }}>
              <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: "var(--color-blood-orange)" }} />
              Run to be remembered
            </div>
            <h1 className="mt-4 text-balance font-display-italic font-display text-[56px] leading-[48px] tracking-[0] text-fg sm:text-[88px] sm:leading-[76px] lg:text-[120px] lg:leading-[100px]">
              CHASE
              <br />
              THE <span style={{ color: "var(--color-acid-lime)" }}>BOOM.</span>
            </h1>
            <p className="mt-6 max-w-[44ch] font-mono text-[12px] uppercase tracking-[0.12em] text-fg-muted">
              Strava-linked race-time predictions, the city leaderboards, the social wall.
              <span className="ml-1" style={{ color: "var(--color-blood-orange)" }}>Every PB is a BOOM.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/me"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[13px] uppercase tracking-[0.08em] text-night transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: "var(--color-acid-lime)" }}
              >
                Connect with Strava
              </Link>
              <Link
                to="/oracle/$raceSlug"
                params={{ raceSlug: heroPrediction.raceSlug }}
                className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[13px] uppercase tracking-[0.08em] text-fg transition-colors hover:border-blood-orange"
                style={{ borderColor: "var(--color-border-strong)" }}
              >
                See the Oracle →
              </Link>
            </div>
          </div>

          <div className="relative hidden min-h-[320px] lg:block">
            <div className="absolute right-6 top-2">
              <BoomVerifiedBadge size={108} />
            </div>
            <div className="absolute right-10 top-32">
              <Sticker tone="lime" rotate={-6}>
                Every PB is a BOOM.
              </Sticker>
            </div>
            <div className="absolute bottom-4 right-8">
              <RunClubBadge size={84} />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-[1.1fr_1fr]">
        <Link
          to="/oracle/$raceSlug"
          params={{ raceSlug: heroPrediction.raceSlug }}
          className="block rounded-[14px] transition-transform hover:-translate-y-0.5"
        >
          <OracleCard prediction={heroPrediction} />
        </Link>
        <div className="rounded-[14px] border border-border bg-bg-elevated">
          <div className="flex items-baseline justify-between border-b border-border px-4 py-4 sm:px-5">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
                Cape Town · 10K · All-time
              </div>
              <h2 className="mt-1 font-display text-[24px] leading-[24px] tracking-[-0.015em] text-fg sm:text-[28px] sm:leading-[28px]">
                CITY LEADERBOARD
              </h2>
            </div>
            <Link
              to="/leaderboards"
              search={{ distance: "10K", city: "Cape Town" }}
              className="font-mono text-[11px] uppercase tracking-[0.1em] text-fg-muted transition-colors hover:text-blood-orange"
            >
              Open all →
            </Link>
          </div>
          {leaderboard.slice(0, 5).map((entry) => (
            <LeaderboardRow
              key={entry.rank}
              entry={entry}
              highlight={entry.runner.handle === heroRunner.handle}
            />
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionLabel eyebrow="Suggested races" title="THE CALENDAR" linkTo="/races" linkLabel="See all races" />
        <div className="mt-4 flex flex-col gap-3">
          {upcomingRaces.map((race) => (
            <Link
              key={race.slug}
              to="/races/$slug"
              params={{ slug: race.slug }}
              className="block transition-transform hover:-translate-y-0.5"
            >
              <RaceCard race={race} />
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionLabel eyebrow="The Wall" title="WHAT'S HAPPENING" linkTo="/wall" linkLabel="Open the Wall" />
        <div className="mt-4 flex flex-col gap-3">
          {wall.map((event) => (
            <WallEvent key={event.id} event={event} />
          ))}
        </div>
      </section>

      <footer className="mt-16 flex items-center justify-between border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-subtle sm:mt-20">
        <span>Every PB is a BOOM.</span>
        <span className="flex items-center gap-3">
          <Link to="/privacy" className="hover:text-fg">Privacy</Link>
          <Link to="/terms" className="hover:text-fg">Terms</Link>
        </span>
      </footer>
    </main>
  );
}

function HeroImage() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <img
        src={HOME_HERO_IMG}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-70 outline outline-1 outline-white/10"
        style={{
          maskImage:
            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,1) 75%)",
          WebkitMaskImage:
            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,1) 75%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-night via-night/80 to-transparent" />
    </div>
  );
}

function TopBar() {
  return (
    <div className="flex items-center justify-between gap-3">
      <Link to="/" className="flex items-center gap-3">
        <Wordmark size="sm" />
      </Link>
      <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
        <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
        <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
        <Link
          to="/leaderboards"
          search={{ distance: "10K", city: "Cape Town" }}
          className="hidden hover:text-fg sm:inline"
        >
          Leaderboards
        </Link>
        <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
        <Link
          to="/me"
          className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: "var(--color-acid-lime)" }}
        >
          Connect Strava
        </Link>
      </nav>
    </div>
  );
}

function SectionLabel({
  eyebrow,
  title,
  linkTo,
  linkLabel,
}: {
  eyebrow: string;
  title: string;
  linkTo?: "/races" | "/wall" | "/oracle" | "/leaderboards";
  linkLabel?: string;
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-border pb-4">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
          {eyebrow}
        </div>
        <h2 className="mt-1 font-display text-[32px] leading-[28px] tracking-[-0.015em] text-fg sm:text-[44px] sm:leading-[40px]">
          {title}
        </h2>
      </div>
      {linkTo && linkLabel ? (
        <Link
          to={linkTo}
          className="font-mono text-[11px] uppercase tracking-[0.1em] text-fg-muted transition-colors hover:text-blood-orange"
        >
          {linkLabel} →
        </Link>
      ) : null}
    </div>
  );
}
